import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import Select from "react-select";
import "react-datepicker/dist/react-datepicker.css";
import AdminSidebar from "../../components/admin/AdminSidebar";
import Modal from "../../components/shared/Modal";
import SmartDataTable from "../../components/shared/SmartDataTable";
import { Search, Bell, Ticket, Plus, X, Loader2, CalendarDays, Users, DollarSign, Trophy, Megaphone } from "lucide-react";
import { poolsApi } from "../../services/api";

function fmtDate(d) {
  return d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
}

const SIZE_OPTIONS = [
  { value: 50,  label: "50 members" },
  { value: 100, label: "100 members" },
  { value: 200, label: "200 members" },
  { value: 300, label: "300 members (default)" },
  { value: 500, label: "500 members" },
];
const ENTRY_OPTIONS = [
  { value: 2,  label: "$2.00" },
  { value: 4,  label: "$4.00 (default)" },
  { value: 5,  label: "$5.00" },
  { value: 10, label: "$10.00" },
  { value: 20, label: "$20.00" },
];

const LIMIT = 25;
const EMPTY = { name: "", drawDate: null, size: SIZE_OPTIONS[3], entryAmount: ENTRY_OPTIONS[1], jackpotEstimate: "" };

export default function AdminPools() {
  const [pools, setPools] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [outcomePool, setOutcomePool] = useState(null);
  const [outcome, setOutcome] = useState("lost");
  const [outcomeNote, setOutcomeNote] = useState("");
  const [announcing, setAnnouncing] = useState(false);
  const [error, setError] = useState("");

  async function load(p = 1) {
    setLoading(true);
    setError("");
    try {
      const res = await poolsApi.getAll({ limit: 1000, page: p });
      setPools(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load pools.");
      setPools([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(page); }, [page]);

  function handleClose() { setOpen(false); setForm(EMPTY); setFormError(""); }

  async function handleCreate(e) {
    e.preventDefault();
    if (!form.name.trim()) { setFormError("Pool name is required."); return; }
    if (!form.drawDate) { setFormError("Draw date and time are required."); return; }
    setSaving(true);
    try {
      await poolsApi.create({
        name: form.name,
        drawDate: form.drawDate.toISOString(),
        size: form.size.value,
        entryAmount: form.entryAmount.value,
        jackpotEstimate: Number(form.jackpotEstimate) || 0,
      });
      handleClose();
      load(1); setPage(1);
    } catch (err) {
      setFormError(err.response?.data?.message || "Failed to create pool.");
    } finally {
      setSaving(false);
    }
  }

  async function handleCancel(id) {
    if (!window.confirm("Cancel this pool? This cannot be undone.")) return;
    try {
      await poolsApi.cancel(id);
      load(page);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to cancel pool.");
    }
  }

  function openOutcomeModal(pool) {
    setOutcomePool(pool);
    setOutcome(pool.outcome && pool.outcome !== "pending" ? pool.outcome : "lost");
    setOutcomeNote(pool.outcomeNote || "");
  }

  async function handleAnnounceOutcome(e) {
    e.preventDefault();
    if (!outcomePool) return;
    setAnnouncing(true);
    try {
      await poolsApi.announceOutcome(outcomePool._id, { outcome, outcomeNote });
      setOutcomePool(null);
      load(page);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to announce outcome.");
    } finally {
      setAnnouncing(false);
    }
  }

  const columns = [
    { name: "Pool ID", selector: (row) => row.poolId || "", sortable: true, cell: (row) => <strong className="table-primary">{row.poolId}</strong> },
    { name: "Name", selector: (row) => row.name || "", sortable: true, grow: 1.25 },
    { name: "Draw Date", selector: (row) => row.drawDate || "", sortable: true, cell: (row) => fmtDate(row.drawDate), grow: 1.2 },
    {
      name: "Members",
      selector: (row) => row.memberCount || 0,
      sortable: true,
      cell: (row) => {
        const pct = row.size ? Math.min((row.memberCount / row.size) * 100, 100) : 0;
        return (
          <span>
            {row.memberCount} / {row.size}
            <i className="mini-progress"><b style={{ width: `${pct}%` }} /></i>
          </span>
        );
      },
    },
    { name: "Entry", selector: (row) => row.entryAmount || 0, sortable: true, cell: (row) => `$${row.entryAmount}` },
    { name: "Status", selector: (row) => row.status || "", sortable: true, cell: (row) => <span className={`table-status ${row.status === "locked" || row.status === "completed" ? "live" : row.status === "cancelled" ? "danger" : "warn"}`}>{row.status}</span> },
    { name: "Outcome", selector: (row) => row.outcome || "pending", sortable: true, cell: (row) => <span className={`table-status ${row.outcome === "won" ? "live" : row.outcome === "lost" ? "danger" : "warn"}`}>{row.outcome || "pending"}</span> },
    {
      name: "Actions",
      button: true,
      cell: (row) => row.status !== "cancelled" ? (
        <span className="row-actions">
          <button onClick={() => handleCancel(row._id)} title="Cancel pool" style={{ color: "var(--danger)" }}>
            <X size={14} />
          </button>
          <button onClick={() => openOutcomeModal(row)} title="Announce pool outcome">
            Outcome
          </button>
        </span>
      ) : null,
      minWidth: "150px",
    },
  ];

  return (
    <div className="admin-shell">
      <AdminSidebar />
      <main className="admin-main">
        <header className="admin-topbar">
          <div className="admin-title">Pools</div>
          <label className="admin-search"><Search size={15} /><input placeholder="Search pools..." /></label>
          <div className="system-live"><i /> System Live</div>
          <button className="icon-button" aria-label="Notifications"><Bell size={18} /><i /></button>
        </header>

        <div className="admin-content">
          {error && <div className="auth-error" style={{ marginBottom: 16 }}>{error}</div>}
          <section className="admin-panel" style={{ padding: 0 }}>
            <div className="panel-heading">
              <h2><Ticket size={18} /> Pool Management
                <span style={{ color: "var(--muted)", fontSize: 13, fontWeight: 400, marginLeft: 8 }}>{total} total</span>
              </h2>
              <button onClick={() => setOpen(true)}><Plus size={14} /> New Pool</button>
            </div>

            <SmartDataTable
              columns={columns}
              data={pools}
              loading={loading}
              searchFields={["poolId", "name", "status", "outcome", "outcomeNote"]}
              searchPlaceholder="Search pools by ID, name, status, outcome..."
              emptyMessage="No pools yet. Create your first Powerball pool."
              paginationPerPage={LIMIT}
            />
          </section>
        </div>
      </main>

      <Modal open={open} onClose={handleClose} title="Create New Pool" subtitle="Set up a new Powerball pool for participants to join" icon={<Ticket size={18} />} size="md">
        <form className="modal-form" onSubmit={handleCreate}>
          {formError && <div className="auth-error">{formError}</div>}

          <div className="auth-field">
            <label>Pool Name</label>
            <input
              value={form.name}
              onChange={(e) => { setForm((f) => ({ ...f, name: e.target.value })); setFormError(""); }}
              placeholder="e.g. Grand Powerball Pool – Jan 2026"
              autoFocus
            />
          </div>

          <div className="auth-field">
            <label><CalendarDays size={13} style={{ marginRight: 6 }} />Powerball Draw Date &amp; Time</label>
            <DatePicker
              selected={form.drawDate}
              onChange={(d) => { setForm((f) => ({ ...f, drawDate: d })); setFormError(""); }}
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              dateFormat="MMM d, yyyy h:mm aa"
              placeholderText="Select draw date and time…"
              minDate={new Date()}
              popperPlacement="bottom-start"
            />
          </div>

          <div className="modal-row">
            <div className="auth-field">
              <label><Users size={13} style={{ marginRight: 6 }} />Pool Size</label>
              <Select
                classNamePrefix="lp-select"
                options={SIZE_OPTIONS}
                value={form.size}
                onChange={(v) => setForm((f) => ({ ...f, size: v }))}
                isSearchable={false}
              />
              <small style={{ color: "var(--muted)", fontSize: 11 }}>Max participants</small>
            </div>
            <div className="auth-field">
              <label><DollarSign size={13} style={{ marginRight: 6 }} />Entry Amount</label>
              <Select
                classNamePrefix="lp-select"
                options={ENTRY_OPTIONS}
                value={form.entryAmount}
                onChange={(v) => setForm((f) => ({ ...f, entryAmount: v }))}
                isSearchable={false}
              />
              <small style={{ color: "var(--muted)", fontSize: 11 }}>Cost per participant</small>
            </div>
          </div>

          <div className="auth-field">
            <label><Trophy size={13} style={{ marginRight: 6 }} />Estimated Jackpot ($) <span className="auth-optional">(optional)</span></label>
            <input
              type="number"
              value={form.jackpotEstimate}
              onChange={(e) => setForm((f) => ({ ...f, jackpotEstimate: e.target.value }))}
              placeholder="e.g. 420000000"
            />
          </div>

          <div className="modal-actions">
            <button type="button" className="modal-btn-cancel" onClick={handleClose}>Cancel</button>
            <button type="submit" className="modal-btn-submit" disabled={saving}>
              {saving ? <><Loader2 size={14} className="spin" /> Creating…</> : <><Plus size={14} /> Create Pool</>}
            </button>
          </div>
        </form>
      </Modal>
      <Modal open={!!outcomePool} onClose={() => setOutcomePool(null)} title={outcomePool ? `Announce Outcome - ${outcomePool.poolId}` : "Announce Outcome"} subtitle="Mark this pool as won or lost for user dashboards" icon={<Megaphone size={18} />} size="sm">
        <form className="modal-form" onSubmit={handleAnnounceOutcome}>
          <div className="auth-field">
            <label>Outcome</label>
            <div className="segmented-control">
              <button type="button" className={outcome === "won" ? "active" : ""} onClick={() => setOutcome("won")}>Won</button>
              <button type="button" className={outcome === "lost" ? "active" : ""} onClick={() => setOutcome("lost")}>Lost</button>
            </div>
          </div>
          <div className="auth-field">
            <label>Admin note</label>
            <input value={outcomeNote} onChange={(e) => setOutcomeNote(e.target.value)} placeholder="Optional internal note" />
          </div>
          <div className="modal-actions">
            <button type="button" className="modal-btn-cancel" onClick={() => setOutcomePool(null)}>Cancel</button>
            <button type="submit" className="modal-btn-submit" disabled={announcing}>
              {announcing ? <><Loader2 size={14} className="spin" /> Announcing...</> : <><Megaphone size={14} /> Announce</>}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
